import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, KeyRound, Loader2, Star } from "lucide-react";
import api, { formatErr, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import MyChecklistSection from "../components/MyChecklistSection";
import TelegramPushCard from "../components/TelegramPushCard";
import CustomerPreferencesSection from "../components/CustomerPreferencesSection";
import PushNotificationsToggle from "../components/PushNotificationsToggle";
import CustomerPinSection from "../components/customer/CustomerPinSection";
import CustomerPinPromptDialog from "../components/customer/CustomerPinPromptDialog";
import GdprSection from "../components/customer/GdprSection";
import { BRAND_COLORS, STATUS_COLORS } from "../lib/bookingStatus";

import PropertySwitcher from "../components/customer/PropertySwitcher";
import { useProperty } from "../lib/PropertyContext";
import { ViewResetSection } from "../components/ViewResetSection";

export default function CustomerSettings() {
  const { lang } = useI18n();
  const { user, refresh } = useAuth();
  const de = lang === "de";
  // Feb 2026 · 8 Sep 2026 · User-Directive „Einstellungen sind objektspezifisch
  // und folgen dem global aktiven Objekt". Wir konsumieren `selected` aus dem
  // `PropertyContext` — Wechsel auf Dashboard reflektiert sich hier sofort.
  const { properties, selected, reload: reloadProperties, resetSelection } = useProperty();
  const [welcomeName, setWelcomeName] = useState(user?.welcome_name || "");
  const [propertyName, setPropertyName] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  // Feb 2026 · 16 Sep 2026 · User-Directive „PIN-Pflicht für Kunden":
  // Bei jedem „Speichern" wird der Kunde nach seiner persönlichen 4-stelligen
  // PIN gefragt. Der Dialog läuft im „confirm"-Modus, falls PIN gesetzt ist,
  // sonst im „setup"-Modus (Backend antwortet mit 428 · code=customer_pin_not_set).
  const [profilePinMode, setProfilePinMode] = useState(null);   // null | "confirm" | "setup"
  const performProfileSave = async (pin) => {
    // Feb 2026 · 8 Sep 2026 · property_id mitschicken, damit das
    // gerade aktive Objekt umbenannt wird (statt Legacy: erstes Objekt).
    await api.patch("/me/profile", {
      welcome_name: welcomeName.trim(),
      property_name: propertyName.trim(),
      property_id: selected?.id || undefined,
      customer_pin: pin,
    });
    await refresh();
    toast.success(de ? "Einstellungen gespeichert." : "Settings saved.");
  };
  // Feb 2026 · 9 Sep 2026 · User-Directive „Objekt-Vorschaubild pro Objekt":
  // Persistiert auf `properties.photo_url` — objektspezifisch. Objektwechsel
  // im PropertySwitcher lädt automatisch das nächste Bild, weil `selected`
  // aus dem globalen PropertyContext kommt.
  const [propertyPhotoBusy, setPropertyPhotoBusy] = useState(false);

  useEffect(() => { setWelcomeName(user?.welcome_name || ""); }, [user?.welcome_name]);

  // Objektname folgt strikt dem aktiven Objekt aus dem Context. Wechsel auf
  // dem Dashboard → Objektname-Feld hier zieht sofort nach.
  useEffect(() => { setPropertyName(selected?.name || ""); }, [selected?.id, selected?.name]);

  // Feb 2026 · 9 Sep 2026 · Objekt-Foto-Handler — jedes Objekt hat
  // seine eigene Bilder-Galerie (property.photos[], max 5). Wir nutzen den
  // gleichen /files/upload → PATCH /properties/{id}-Flow wie im Team-Bereich,
  // aber scoped auf das aktuell aktive Objekt aus dem PropertyContext. Der
  // Backend-Guard in `properties_router.update_property` prüft
  // `owner_id == user.id` — d.h. Kunden können ausschließlich ihre eigenen
  // Objekte bebildern; fremde Objekte werfen 403.
  const galleryPhotos = (() => {
    const arr = Array.isArray(selected?.photos) ? selected.photos.filter(Boolean) : [];
    if (arr.length) return arr;
    if (selected?.photo_url) return [selected.photo_url];
    return [];
  })();
  const canAddPhoto = galleryPhotos.length < 5;

  const uploadPropertyPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ""; // Re-Upload derselben Datei erlauben
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(de ? "Bitte eine Bilddatei wählen." : "Please pick an image."); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error(de ? "Bild ist zu groß (max. 8 MB)." : "Image too large (8 MB max)."); return; }
    if (!selected?.id) { toast.error(de ? "Kein Objekt aktiv." : "No property selected."); return; }
    if (!canAddPhoto) { toast.error(de ? "Maximal 5 Bilder pro Objekt." : "Max 5 images per property."); return; }
    setPropertyPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newPhotos = [...galleryPhotos, up.id];
      await api.patch(`/properties/${selected.id}`, { photos: newPhotos });
      await reloadProperties();
      toast.success(de
        ? `Bild ${newPhotos.length}/5 hinzugefügt.`
        : `Photo ${newPhotos.length}/5 added.`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setPropertyPhotoBusy(false); }
  };

  const removePropertyPhoto = async (index) => {
    if (!selected?.id) return;
    setPropertyPhotoBusy(true);
    try {
      const newPhotos = galleryPhotos.filter((_, i) => i !== index);
      await api.patch(`/properties/${selected.id}`, { photos: newPhotos });
      await reloadProperties();
      toast.success(de ? "Bild entfernt." : "Photo removed.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setPropertyPhotoBusy(false); }
  };

  const promotePhotoToCover = async (index) => {
    // Position 0 = Cover — auf Dashboard erstes rotierendes Bild.
    if (!selected?.id || index === 0) return;
    setPropertyPhotoBusy(true);
    try {
      const reordered = [
        galleryPhotos[index],
        ...galleryPhotos.filter((_, i) => i !== index),
      ];
      await api.patch(`/properties/${selected.id}`, { photos: reordered });
      await reloadProperties();
      toast.success(de ? "Als Cover festgelegt." : "Set as cover.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setPropertyPhotoBusy(false); }
  };

  const saveProfile = async () => {
    if (!welcomeName.trim()) { toast.error(de ? "Begrüßungsname darf nicht leer sein." : "Welcome name required."); return; }
    if (!propertyName.trim()) { toast.error(de ? "Objektname darf nicht leer sein." : "Property name required."); return; }
    // Feb 2026 · 16 Sep 2026 · Vor dem PATCH prüfen, ob der Kunde eine
    // PIN hat — Backend würde sonst 428 werfen. Statt eines Fehler-Toasts
    // öffnen wir direkt den Set-PIN-Dialog.
    try {
      const { data } = await api.get("/me/customer-pin/status");
      setProfilePinMode(data?.is_set ? "confirm" : "setup");
    } catch {
      setProfilePinMode("confirm");
    }
  };

  return (
    <div className="max-w-[700px] mx-auto px-5 md:px-10 py-8 md:py-14 pb-32" data-testid="customer-settings">
      <div className="mb-8">
        <div className="label-caps mb-3">Weissblom</div>
        <h1 className="font-display text-3xl md:text-5xl text-navy">
          {de ? "Einstellungen" : "Preferences"}
        </h1>
        <p className="text-[14px] text-navy-dark/70 mt-3">
          {de
            ? "Passen Sie Ihren Begrüßungsnamen und Ihre Objektangaben an."
            : "Update your welcome name and property details."}
        </p>
      </div>

      {/* Feb 2026 · 8 Sep 2026 · Objekt-Umschalter (global synchron mit
          Dashboard). Rendert automatisch nichts bei Ein-Objekt-Kunden. */}
      <PropertySwitcher testidPrefix="settings-property-switcher" />

      {properties && properties.length > 1 && selected && (
        <p className="text-[12px] text-navy-dark/60 -mt-2 mb-6 italic" data-testid="settings-property-context-hint">
          {de
            ? `Die folgenden Einstellungen gelten für: ${selected.name}`
            : `The following preferences apply to: ${selected.name}`}
        </p>
      )}

      <section className="bg-white border border-navy/10 p-6 md:p-8 space-y-8" data-testid="profile-edit-section">
        {/* Welcome name */}
        <div>
          <label htmlFor="settings-welcome-name" className="label-caps block mb-2">{de ? "Begrüßungsname" : "Welcome name"}</label>
          <input
            id="settings-welcome-name"
            data-testid="settings-welcome-name"
            value={welcomeName}
            onChange={(e) => setWelcomeName(e.target.value)}
            placeholder={de ? "z. B. Familie Sørensen" : "e.g. Sørensen family"}
            className="w-full border border-navy/15 px-4 py-3 text-[14px] text-navy-dark focus:border-gold focus:outline-none"
          />
          <p className="text-[11.5px] text-navy-dark/55 mt-2 italic">
            {de ? "Erscheint auf Ihrem Home-Screen." : "Shown on your home screen."}
          </p>
        </div>

        {/* Property name */}
        <div>
          <label htmlFor="settings-property-name" className="label-caps block mb-2">{de ? "Objektname" : "Property name"}</label>
          <input
            id="settings-property-name"
            data-testid="settings-property-name"
            value={propertyName}
            onChange={(e) => setPropertyName(e.target.value)}
            placeholder={de ? "z. B. Pfaffensteig" : "e.g. Pfaffensteig"}
            className="w-full border border-navy/15 px-4 py-3 text-[14px] text-navy-dark focus:border-gold focus:outline-none"
          />
          <p className="text-[11.5px] text-navy-dark/55 mt-2 italic">
            {de ? "Wird als Objektname im Dashboard und in Berichten verwendet." : "Used as the property name on the dashboard and reports."}
          </p>
        </div>

        {/* Feb 2026 · 9 Sep 2026 · Objekt-Foto-Galerie (Property.photos[]) —
            bis zu 5 Bilder pro Objekt. Position 0 dient als Cover — auf dem
            Dashboard rotieren alle Bilder alle 5 Sekunden mit Fade-Übergang. */}
        <div data-testid="settings-property-photos-block">
          <div className="label-caps mb-2 flex items-center gap-2 flex-wrap">
            <span>{de ? "Objekt-Bildergalerie" : "Property gallery"}</span>
            {selected?.name && (
              <span
                className="normal-case tracking-normal text-navy-dark/60 text-[11.5px]"
                data-testid="settings-property-photos-context"
              >
                {de ? "für" : "for"} „{selected.name}" · {galleryPhotos.length}/5
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {galleryPhotos.map((fid, idx) => (
              <div
                key={`${fid}-${idx}`}
                className="relative aspect-[16/10] border border-navy/15 overflow-hidden group"
                data-testid={`settings-property-photo-slot-${idx}`}
              >
                <img
                  src={fileUrl(fid)}
                  alt={`Bild ${idx + 1}`}
                  className="w-full h-full object-cover"
                  data-testid={`settings-property-photo-img-${idx}`}
                />
                {idx === 0 ? (
                  <span
                    className="absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[9.5px] tracking-[0.14em] uppercase font-semibold text-ivory shadow-sm"
                    style={{ background: BRAND_COLORS.gold }}
                    data-testid="settings-property-photo-cover-badge"
                  >
                    Cover
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => promotePhotoToCover(idx)}
                    disabled={propertyPhotoBusy}
                    data-testid={`settings-property-photo-cover-${idx}`}
                    className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 text-[9.5px] tracking-[0.14em] uppercase text-navy bg-white/90 hover:bg-white border border-navy/20 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                  >
                    {de ? "Als Cover" : "Set cover"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removePropertyPhoto(idx)}
                  disabled={propertyPhotoBusy}
                  data-testid={`settings-property-photo-remove-${idx}`}
                  className="absolute top-1.5 right-1.5 bg-white/95 hover:bg-white text-navy p-1 border border-navy/20 disabled:opacity-60"
                  aria-label={de ? "Bild entfernen" : "Remove image"}
                >
                  <X className="w-3.5 h-3.5" strokeWidth={1.6} />
                </button>
              </div>
            ))}
            {selected?.id && canAddPhoto && (
              <label
                htmlFor="settings-property-photo-input"
                data-testid="settings-property-photo-upload-label"
                className="aspect-[16/10] flex flex-col items-center justify-center gap-2 border border-dashed border-navy/25 hover:border-gold cursor-pointer transition-colors bg-ivory/40"
              >
                <ImagePlus className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.2} />
                <div className="label-caps text-center px-2">
                  {propertyPhotoBusy
                    ? (de ? "Wird hochgeladen…" : "Uploading…")
                    : (de ? "Bild hinzufügen" : "Add image")}
                </div>
                <input
                  id="settings-property-photo-input"
                  data-testid="settings-property-photo-file-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={uploadPropertyPhoto}
                  disabled={propertyPhotoBusy || !selected?.id}
                />
              </label>
            )}
          </div>
          {selected?.id ? (
            <p className="text-[11.5px] text-navy-dark/55 mt-2 italic">
              {de
                ? 'Bis zu 5 Bilder pro Objekt. Das erste Bild („Cover") rotiert auf dem Dashboard mit den anderen. Bilder sind objektspezifisch.'
                : 'Up to 5 images per property. The first image ("Cover") rotates on the dashboard together with the others. Images are stored per property.'}
            </p>
          ) : (
            <div
              data-testid="settings-property-no-active-object"
              className="mt-2 text-[13px] text-navy-dark/70 bg-ivory/60 border border-dashed border-navy/25 px-4 py-3 leading-relaxed"
            >
              {de
                ? "Für dieses Konto ist derzeit kein aktives Objekt hinterlegt, daher können keine Bilder hochgeladen werden. Bitte wenden Sie sich an Ihr Weissblom-Team, um Ihr Objekt zu aktivieren."
                : "There is currently no active property on this account, so photos cannot be uploaded. Please contact your Weissblom team to activate your property."}
            </div>
          )}
        </div>

        {/* Feb 2026 · 9 Sep 2026 · Reihenfolge (User-Directive):
            Bildergalerie → Rechnungsadresse für dieses Objekt → Hauptobjekt.
            Der Startbild-Block wurde entfernt (9 Sep 2026) — Kunden pflegen
            visuelle Objekt-Repräsentation ausschließlich über die
            Objekt-Bildergalerie oberhalb. */}

        {/* Feb 2026 · 9 Sep 2026 · Objektspezifische Rechnungsadresse
            (Property.billing_address) — optionale Objekt-Override. Wenn leer,
            fällt die Rechnung auf die Kunden-Standardadresse zurück. Damit
            kann z.B. eine Ferienwohnung an einen Verwalter fakturiert werden
            während das Hauptobjekt an den Kunden geht. */}
        <div data-testid="settings-property-billing-block">
          <div className="label-caps mb-2 flex items-center gap-2 flex-wrap">
            <span>{de ? "Rechnungsadresse für dieses Objekt" : "Property-specific billing address"}</span>
            {selected?.name && (
              <span
                className="normal-case tracking-normal text-navy-dark/60 text-[11.5px]"
                data-testid="settings-property-billing-context"
              >
                {de ? "für" : "for"} „{selected.name}"
              </span>
            )}
          </div>
          <PropertyBillingForm
            key={selected?.id || "none"}
            selected={selected}
            properties={properties}
            reloadProperties={reloadProperties}
            de={de}
          />
          <p className="text-[11.5px] text-navy-dark/55 mt-2 italic">
            {de
              ? "Optional. Wenn nicht gesetzt, verwendet Weissblom automatisch Ihre Standard-Rechnungsadresse für Rechnungen zu diesem Objekt."
              : "Optional. If not set, Weissblom automatically uses your default billing address for invoices related to this property."}
          </p>
        </div>

        {/* Feb 2026 · 9 Sep 2026 · User-Directive „Hauptobjekt":
            Kunde markiert genau EIN Objekt als Hauptobjekt. Der Admin-
            Bereich zoomt seine Präferenzen-Karte auf dieses Objekt. */}
        <PropertyPrimaryToggle
          selected={selected}
          properties={properties}
          reloadProperties={reloadProperties}
          de={de}
        />

        <div className="pt-4 border-t border-navy/10">
          <button
            type="button"
            data-testid="settings-save-profile-btn"
            onClick={saveProfile}
            disabled={profileSaving}
            className="w-full md:w-auto bg-navy text-ivory px-8 py-3.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 transition-opacity disabled:opacity-60"
            style={{ background: BRAND_COLORS.navy }}
          >
            {profileSaving ? (de ? "Wird gespeichert…" : "Saving…") : (de ? "Speichern" : "Save")}
          </button>
        </div>
      </section>

      {/* Feb 2026 · User-Directive „Customer-PIN":
          Persönliche 4-stellige PIN — Digitale Signatur für Angebote. */}
      <div className="mt-8" data-testid="customer-pin-wrap">
        <CustomerPinSection />
      </div>

      {/* Personal preferences (allergies, food, home setup) — synced to the
          concierge team with throttled notifications. */}
      <div className="mt-8" data-testid="customer-preferences-wrap">
        <CustomerPreferencesSection propertyId={selected?.id} propertyName={properties && properties.length > 1 ? selected?.name : undefined} properties={properties} />
        <div className="mt-6"><PushNotificationsToggle /></div>
      </div>

      {/* Feb 2026 · 16 Sep 2026 · User-Directive „PIN-Pflicht für Kunden":
          Bei jedem Profil-Save öffnet sich dieser Dialog. „confirm" wenn
          PIN gesetzt, „setup" wenn noch nicht — beide Wege gehen nach
          Success denselben `performProfileSave(pin)`-Pfad. */}
      <CustomerPinPromptDialog
        open={!!profilePinMode}
        mode={profilePinMode || "confirm"}
        de={de}
        title={profilePinMode === "setup"
          ? (de ? "Einmalig PIN einrichten" : "Set up your PIN once")
          : (de ? "Speichern mit PIN bestätigen" : "Confirm save with PIN")}
        description={profilePinMode === "setup"
          ? (de
              ? "Vergeben Sie zuerst Ihre 4-stellige PIN — sie schützt jede zukünftige Profil- und Buchungsänderung."
              : "Choose your 4-digit PIN first — it protects every future profile or booking change.")
          : (de
              ? "Bitte bestätigen Sie die Änderung mit Ihrer persönlichen 4-stelligen PIN."
              : "Please confirm the change with your personal 4-digit PIN.")}
        submitLabel={de ? "Speichern" : "Save"}
        onClose={() => setProfilePinMode(null)}
        onConfirm={async (pin) => {
          setProfileSaving(true);
          try {
            await performProfileSave(pin);
            setProfilePinMode(null);
          } finally {
            setProfileSaving(false);
          }
        }}
        onSuccess={async (newPin) => {
          // Nach frischer Setup-PIN direkt speichern — kein zweiter Dialog.
          setProfileSaving(true);
          try {
            await performProfileSave(newPin);
            setProfilePinMode(null);
          } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || err.message);
          } finally {
            setProfileSaving(false);
          }
        }}
      />

      {/* Personal departure & arrival checklist */}
      <div className="mt-8">
        <MyChecklistSection propertyId={selected?.id} propertyName={properties && properties.length > 1 ? selected?.name : undefined} />
      </div>

      {/* Telegram-Push-Setup — spiegelt den Team-Flow, damit Kunden die
          automatische Willkommens-Nachricht + Status-Updates auch als Push
          auf ihr Handy erhalten (nicht nur In-App). */}
      <div className="mt-8" data-testid="telegram-push-section">
        <TelegramPushCard variant="customer" />
      </div>

      <div className="mt-8">
        <ViewResetSection
          testId="settings-view-reset"
          title={de ? "Ansicht auf Standard zurücksetzen" : "Reset view to defaults"}
          description={de
            ? "Setzt die gespeicherte Objekt-Auswahl zurück — die App startet danach wieder mit Ihrem Hauptobjekt. Ihre Daten, Präferenzen und Checklisten bleiben unverändert."
            : "Resets the saved property selection — the app will open with your primary property again. Your data, preferences and checklists stay untouched."}
          buttonLabel={de ? "Objekt-Auswahl zurücksetzen" : "Reset property selection"}
          onReset={resetSelection}
        />
      </div>
    </div>
  );
}


// ---------- Feb 2026 · 9 Sep 2026 · PropertyBillingForm (PIN-gated) ----------
function PropertyBillingForm({ selected, properties, reloadProperties, de }) {
  const initial = () => {
    const ba = selected?.billing_address || {};
    return {
      street: ba.street || "",
      zip: ba.zip || "",
      city: ba.city || "",
      country: ba.country || "",
    };
  };
  const [form, setForm] = useState(initial);
  const [applyAll, setApplyAll] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset bei Objektwechsel läuft über `key={selected?.id}` am Aufrufer (Remount).

  const trimmed = {
    street: form.street.trim(),
    zip: form.zip.trim(),
    city: form.city.trim(),
    country: form.country.trim(),
  };
  const isComplete = !!(trimmed.street && trimmed.zip && trimmed.city && trimmed.country);
  const isEmpty = !trimmed.street && !trimmed.zip && !trimmed.city && !trimmed.country;
  const original = selected?.billing_address || null;
  const hasChanges =
    !original
      ? !isEmpty
      : (original.street || "") !== trimmed.street
        || (original.zip || "") !== trimmed.zip
        || (original.city || "") !== trimmed.city
        || (original.country || "") !== trimmed.country;
  // „Für alle" zählt immer als Änderung, weil andere Objekte betroffen sind
  const canOpenPin = ((hasChanges) || applyAll) && (isComplete || isEmpty) && !busy;
  const showApplyAll = Array.isArray(properties) && properties.length > 1;

  const openPin = () => {
    if (!canOpenPin) return;
    setPin("");
    setPinOpen(true);
  };

  const submitWithPin = async () => {
    if (!/^\d{4}$/.test(pin)) { toast.error(de ? "PIN muss 4 Ziffern haben." : "PIN must be 4 digits."); return; }
    setBusy(true);
    try {
      const payload = isEmpty ? null : trimmed;
      if (applyAll) {
        const { data } = await api.post("/properties/apply-billing-to-all", {
          billing_address: payload,
          customer_pin: pin,
        });
        await reloadProperties();
        setPinOpen(false);
        toast.success(de
          ? `Rechnungsadresse für ${data.updated} Objekte gespeichert.`
          : `Billing address applied to ${data.updated} properties.`);
      } else {
        await api.patch(`/properties/${selected.id}`, {
          billing_address: payload,
          customer_pin: pin,
        });
        await reloadProperties();
        setPinOpen(false);
        toast.success(isEmpty
          ? (de ? "Rechnungsadresse zurückgesetzt (fällt auf Kunden-Standard zurück)." : "Billing address cleared (falls back to default).")
          : (de ? "Rechnungsadresse gespeichert." : "Billing address saved."));
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) toast.error(de ? "PIN ist falsch." : "PIN incorrect.");
      else if (status === 400) toast.error(formatErr(err.response?.data?.detail) || err.message);
      else toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <input
          data-testid="settings-property-billing-street"
          value={form.street}
          onChange={(e) => setForm({ ...form, street: e.target.value })}
          placeholder={de ? "Straße & Hausnummer" : "Street & number"}
          className="md:col-span-6 w-full border border-navy/15 px-4 py-3 text-[14px] text-navy-dark focus:border-gold focus:outline-none"
        />
        <input
          data-testid="settings-property-billing-zip"
          value={form.zip}
          onChange={(e) => setForm({ ...form, zip: e.target.value })}
          placeholder={de ? "PLZ" : "ZIP"}
          className="md:col-span-2 w-full border border-navy/15 px-4 py-3 text-[14px] text-navy-dark focus:border-gold focus:outline-none tabular-nums"
        />
        <input
          data-testid="settings-property-billing-city"
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
          placeholder={de ? "Stadt" : "City"}
          className="md:col-span-4 w-full border border-navy/15 px-4 py-3 text-[14px] text-navy-dark focus:border-gold focus:outline-none"
        />
        <input
          data-testid="settings-property-billing-country"
          value={form.country}
          onChange={(e) => setForm({ ...form, country: e.target.value })}
          placeholder={de ? "Land" : "Country"}
          className="md:col-span-6 w-full border border-navy/15 px-4 py-3 text-[14px] text-navy-dark focus:border-gold focus:outline-none"
        />
      </div>

      {showApplyAll && (
        <label
          className="flex items-center gap-2 mt-3 text-[13px] text-navy-dark/80 cursor-pointer select-none"
          data-testid="settings-property-billing-apply-all-label"
        >
          <input
            type="checkbox"
            checked={applyAll}
            onChange={(e) => setApplyAll(e.target.checked)}
            data-testid="settings-property-billing-apply-all-checkbox"
            className="w-4 h-4 accent-navy"
          />
          {de
            ? "Für alle Objekte dieselbe Rechnungsadresse verwenden"
            : "Use the same billing address for all my properties"}
        </label>
      )}

      {!isComplete && !isEmpty && (
        <p
          data-testid="settings-property-billing-incomplete-hint"
          className="text-[11.5px] mt-2"
          style={{ color: STATUS_COLORS.red }}
        >
          {de
            ? "Alle vier Felder sind Pflichtfelder — oder alle vier leer lassen, um die Objekt-Adresse zu entfernen."
            : "All four fields are required — or leave all four empty to remove the property-specific address."}
        </p>
      )}

      <div className="flex items-center gap-3 mt-3">
        <button
          type="button"
          onClick={openPin}
          disabled={!canOpenPin}
          data-testid="settings-property-billing-save-btn"
          className="inline-flex items-center gap-2 bg-navy text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-50"
          style={{ background: BRAND_COLORS.navy }}
        >
          <KeyRound className="w-3.5 h-3.5" strokeWidth={1.8} />
          {de ? "Speichern (mit PIN)" : "Save (with PIN)"}
        </button>
        {original && (
          <span className="text-[11px] text-navy-dark/55 italic" data-testid="settings-property-billing-source">
            {de ? "Aktuell: objektspezifische Adresse aktiv" : "Currently: property-specific override active"}
          </span>
        )}
      </div>

      {pinOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(13,25,48,0.6)" }}
          onClick={() => setPinOpen(false)}
          data-testid="settings-property-billing-pin-modal"
        >
          <div
            role="dialog"
            aria-modal="true"
            className="bg-white w-full max-w-md relative p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPinOpen(false)}
              className="absolute top-3 right-3 text-navy-dark/50 hover:text-navy"
              data-testid="settings-property-billing-pin-close"
              aria-label={de ? "Schliessen" : "Close"}
            >
              <X className="w-4 h-4" strokeWidth={1.6} />
            </button>
            <h2 className="font-display text-2xl text-navy leading-tight mb-1">
              {de ? "PIN-Bestätigung" : "Confirm with PIN"}
            </h2>
            <p className="text-[13px] text-navy-dark/70 leading-relaxed mb-4">
              {applyAll
                ? (de
                  ? `Adresse wird auf alle ${properties?.length ?? 0} Objekte übernommen.`
                  : `Address will be applied to all ${properties?.length ?? 0} properties.`)
                : (de
                  ? `Änderung wird ausschließlich für „${selected?.name}" gespeichert.`
                  : `Change will apply only to "${selected?.name}".`)}
            </p>
            <div className="mb-4">
              <label className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 block mb-1.5">
                {de ? "Persönliche PIN" : "Personal PIN"}
              </label>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                autoFocus
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitWithPin(); } }}
                placeholder={de ? "4 Ziffern" : "4 digits"}
                data-testid="settings-property-billing-pin-input"
                className="w-32 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[14px] tabular-nums tracking-widest text-navy"
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setPinOpen(false)}
                className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2"
                data-testid="settings-property-billing-pin-cancel"
              >
                {de ? "Abbrechen" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={submitWithPin}
                disabled={busy || pin.length !== 4}
                data-testid="settings-property-billing-pin-confirm"
                className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
                style={{ background: BRAND_COLORS.navy }}
              >
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
                {de ? "Bestätigen" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ---------- Kunden-Einstellungen Hauptkomponente ----------
// (Ende der Datei — PropertyBillingForm ist als sibling-Function oberhalb
// exportfähig gemacht, aber wird nur intern von CustomerSettings genutzt.)


// ---------- Feb 2026 · 9 Sep 2026 · PropertyPrimaryToggle (PIN-gated) ----------
// „Als Hauptobjekt festlegen": Kunde markiert genau EIN Objekt als
// Hauptobjekt. Das Admin-Präferenzen-Panel zieht dann die Präferenzen
// dieses Objekts (siehe backend/routers/customer_preferences.py::
// team_get_customer_preferences). Setzen ist durch die 4-stellige
// Customer-PIN geschützt — konsistent mit der Rechnungsadresse.
function PropertyPrimaryToggle({ selected, properties, reloadProperties, de }) {
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  if (!selected?.id) return null;
  // Wenn der Kunde nur ein Objekt hat, ist die Hauptobjekt-Wahl trivial
  // (impliziert). Wir zeigen den Block gar nicht erst.
  if ((properties?.length || 0) < 2) return null;

  const isPrimary = !!selected.is_primary;

  const openPin = () => {
    if (isPrimary) return; // no-op if already primary
    setPin("");
    setPinOpen(true);
  };

  const submitWithPin = async () => {
    if (!/^\d{4}$/.test(pin)) {
      toast.error(de ? "PIN muss 4 Ziffern haben." : "PIN must be 4 digits.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/properties/${selected.id}`, {
        is_primary: true,
        customer_pin: pin,
      });
      await reloadProperties();
      setPinOpen(false);
      toast.success(de
        ? `„${selected.name}" ist jetzt Ihr Hauptobjekt.`
        : `"${selected.name}" is now your primary property.`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) toast.error(de ? "PIN ist falsch." : "PIN incorrect.");
      else toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="settings-property-primary-block">
      <div className="label-caps mb-2 flex items-center gap-2 flex-wrap">
        <span>{de ? "Hauptobjekt" : "Primary property"}</span>
        {selected?.name && (
          <span
            className="normal-case tracking-normal text-navy-dark/60 text-[11.5px]"
            data-testid="settings-property-primary-context"
          >
            {de ? "für" : "for"} „{selected.name}"
          </span>
        )}
      </div>
      <div
        className="flex items-start gap-3 border p-4 md:p-5 bg-white"
        style={{ borderColor: isPrimary ? BRAND_COLORS.gold : "rgba(13,25,48,0.15)" }}
      >
        <Star
          className="w-6 h-6 shrink-0 mt-0.5"
          strokeWidth={1.6}
          style={{ color: isPrimary ? BRAND_COLORS.gold : "rgba(13,25,48,0.35)" }}
          {...(isPrimary ? { fill: BRAND_COLORS.gold } : {})}
        />
        <div className="flex-1 min-w-0">
          {isPrimary ? (
            <div data-testid="settings-property-primary-active">
              <div className="text-[14px] text-navy font-medium">
                {de ? "Dieses Objekt ist Ihr Hauptobjekt." : "This is your primary property."}
              </div>
              <div className="text-[12px] text-navy-dark/65 mt-1 leading-relaxed">
                {de
                  ? "Ihr Concierge-Team sieht die Präferenzen dieses Objekts im Kundenprofil zuerst. Wechsel jederzeit über ein anderes Objekt möglich."
                  : "Your concierge team sees this property's preferences first in the customer profile. You can switch anytime from another property."}
              </div>
            </div>
          ) : (
            <div>
              <div className="text-[14px] text-navy font-medium">
                {de ? "Dieses Objekt als Hauptobjekt festlegen?" : "Set this property as your primary?"}
              </div>
              <div className="text-[12px] text-navy-dark/65 mt-1 leading-relaxed">
                {de
                  ? "Ihr Concierge-Team sieht dann im Kundenprofil die Präferenzen dieses Objekts (statt der eines anderen). Sie können jederzeit wechseln."
                  : "Your concierge team will see this property's preferences in your customer profile (instead of another). You can switch anytime."}
              </div>
              <button
                type="button"
                onClick={openPin}
                disabled={busy}
                data-testid="settings-property-primary-set-btn"
                className="mt-3 inline-flex items-center gap-2 bg-navy text-ivory px-5 py-2 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-50"
                style={{ background: BRAND_COLORS.navy }}
              >
                <KeyRound className="w-3.5 h-3.5" strokeWidth={1.8} />
                {de ? "Als Hauptobjekt festlegen (mit PIN)" : "Set as primary (with PIN)"}
              </button>
            </div>
          )}
        </div>
      </div>

      {pinOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(13,25,48,0.6)" }}
          onClick={() => setPinOpen(false)}
          data-testid="settings-property-primary-pin-modal"
        >
          <div
            role="dialog"
            aria-modal="true"
            className="bg-white w-full max-w-md relative p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPinOpen(false)}
              className="absolute top-3 right-3 text-navy-dark/50 hover:text-navy"
              data-testid="settings-property-primary-pin-close"
              aria-label={de ? "Schliessen" : "Close"}
            >
              <X className="w-4 h-4" strokeWidth={1.6} />
            </button>
            <h2 className="font-display text-2xl text-navy leading-tight mb-1">
              {de ? "PIN-Bestätigung" : "Confirm with PIN"}
            </h2>
            <p className="text-[13px] text-navy-dark/70 leading-relaxed mb-4">
              {de
                ? `„${selected?.name}" wird als Hauptobjekt gesetzt.`
                : `"${selected?.name}" will become your primary property.`}
            </p>
            <div className="mb-4">
              <label className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 block mb-1.5">
                {de ? "Persönliche PIN" : "Personal PIN"}
              </label>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                autoFocus
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitWithPin(); } }}
                placeholder={de ? "4 Ziffern" : "4 digits"}
                data-testid="settings-property-primary-pin-input"
                className="w-32 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[14px] tabular-nums tracking-widest text-navy"
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setPinOpen(false)}
                className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2"
                data-testid="settings-property-primary-pin-cancel"
              >
                {de ? "Abbrechen" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={submitWithPin}
                disabled={busy || pin.length !== 4}
                data-testid="settings-property-primary-pin-confirm"
                className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
                style={{ background: BRAND_COLORS.navy }}
              >
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
                {de ? "Bestätigen" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
